import React, { useState, useEffect } from "react";
import { confirmSignUp, resendSignUpCode } from "@aws-amplify/auth";
import {
  Box,
  Button,
  Card as MuiCard,
  CssBaseline,
  Divider,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import { styled } from "@mui/system";
import { normalizeEmail } from "../../utils/email-utils";
import StripyBackground from "../../views/login/DiagonalCanvas";
import { useTenantConfig } from "../../contexts/TenantConfigContext";

interface ConfirmSignUpProps {
  email: string;
  onConfirmSuccess: () => void;
  onConfirmFailure: (error: string) => void;
}

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
  },
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
}));

const Container = styled(Stack)(({ theme }) => ({
  height: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(2),
  backgroundColor: "transparent",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
}));

const ConfirmSignUp: React.FC<ConfirmSignUpProps> = ({
  email,
  onConfirmSuccess,
  onConfirmFailure,
}) => {
  const { config } = useTenantConfig();
  const [code, setCode] = useState("");
  const [userEmail, setUserEmail] = useState(email || "");
  const [emailEditable, setEmailEditable] = useState(!email); // Allow editing if no email provided
  const [error, setError] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false); // Loading state for confirmation

  // Check for email in URL parameters as fallback
  useEffect(() => {
    if (!email) {
      const urlParams = new URLSearchParams(window.location.search);
      const emailFromUrl = urlParams.get('email');
      if (emailFromUrl) {
        setUserEmail(emailFromUrl);
        setEmailEditable(false); // If we got email from URL, don't make it editable
      }
    } else {
      // If we have email from props, use it and make sure it's updated
      setUserEmail(email);
      setEmailEditable(false);
    }
  }, [email]);

  // Handler for confirming the sign-up
  const handleConfirm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    
    // Validate email is provided
    if (!userEmail || !userEmail.trim()) {
      setError("Please enter your email address");
      return;
    }
    
    // Validate confirmation code is provided
    if (!code || !code.trim()) {
      setError("Please enter your confirmation code");
      return;
    }
    
    // Normalize email to lowercase to prevent case sensitivity issues
    const normalizedEmail = normalizeEmail(userEmail);
    
    setIsConfirming(true);
    try {
      await confirmSignUp({ username: normalizedEmail, confirmationCode: code });
      onConfirmSuccess();
    } catch (err: any) {
      console.error("Error in confirmSignUp:", err);
      setError(err.message || "Error confirming sign up");
      onConfirmFailure(err.message || "Error confirming sign up");
    } finally {
      setIsConfirming(false);
    }
  };

  // Handler for resending the confirmation code
  const handleResend = async () => {
    // Validate email is provided before resending
    if (!userEmail || !userEmail.trim()) {
      setError("Please enter your email address to resend the code");
      return;
    }
    
    // Normalize email to lowercase to prevent case sensitivity issues
    const normalizedEmail = normalizeEmail(userEmail);
    
    setIsResending(true);
    setError("");
    setResendMessage("");
    try {
      await resendSignUpCode({ username: normalizedEmail });
      setResendMessage(
        "If you do not receive an email within a few minutes, your account may not be waiting on email verification — try Sign in with your existing password instead."
      );
    } catch (err: any) {
      console.error("Error in resendSignUpCode:", err);
      setError(err.message || "Error resending confirmation code");
    }
    setIsResending(false);
  };

  return (
    <>
      <StripyBackground />
      <CssBaseline />
      <Box
        sx={{
          position: "absolute",
          top: "1rem",
          left: "1rem",
          zIndex: 2,
          color: "#fff",
          fontWeight: "bold",
          fontSize: "1.5rem",
          display: "flex",
          alignItems: "center",
          gap: "20px",
        }}
      >
        <img src={config?.branding.logoUrl || "/brand/app-icon.svg"} alt={`${config?.branding.displayName || 'AI-Scribe'} Logo`} height="32" style={{ width: 'auto', borderRadius: '4px' }} />
        {config?.branding.displayName || 'AI-SCRIBE'}
      </Box>
      <Container>
        <Card>
          <Typography component="h1" variant="h4" sx={{ textAlign: "center" }}>
            Confirm Sign Up
          </Typography>
          <Alert severity="info" sx={{ textAlign: "left" }}>
            <Typography variant="body2">
              Use this page only if you <strong>registered here</strong> and Cognito sent a verification code.
              If you were invited from another product, go to <strong>Sign in</strong> with your existing email and password
              (or use <strong>Forgot password</strong>). Resend code does nothing for admin-created accounts.
            </Typography>
          </Alert>
          {emailEditable && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 2 }}>
              Please enter your email address and the verification code you received.
            </Typography>
          )}
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          {resendMessage && (
            <Typography variant="body2" color="success.main">
              {resendMessage}
            </Typography>
          )}
          <Box
            component="form"
            onSubmit={handleConfirm}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {emailEditable && (
              <FormControl>
                <FormLabel>Email Address</FormLabel>
                <TextField
                  id="email"
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  fullWidth
                />
              </FormControl>
            )}
            <FormControl>
              <FormLabel>Confirmation Code</FormLabel>
              <TextField
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter your confirmation code"
                required
                fullWidth
              />
            </FormControl>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={isConfirming}
              sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              {isConfirming ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ marginRight: 1 }} />
                  Confirming...
                </>
              ) : (
                "Confirm"
              )}
            </Button>
          </Box>
          <Divider sx={{ my: 2 }}>or</Divider>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2">Didn't receive a code?</Typography>
            <Button onClick={handleResend} disabled={isResending} color="primary">
              {isResending ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ marginRight: 1 }} />
                  Resending...
                </>
              ) : (
                "Resend Code"
              )}
            </Button>
          </Box>
        </Card>
      </Container>
    </>
  );
};

export default ConfirmSignUp;
